import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Wifi, WifiOff, Gift, ExternalLink, Loader2, ChevronRight, CheckCircle2, ArrowLeft } from 'lucide-react';
import { LanguageGate } from '../components/shared/LanguageGate';
import { RoomFeedbackGuestFlow } from '../components/hotel/RoomFeedbackGuestFlow';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PhotoAttach from './reviewShield/PhotoAttach';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const HotelGuestFlow = () => {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [lang, setLang] = useState('en');
    const [langGateDone, setLangGateDone] = useState(false);
    const [screen, setScreen] = useState('loading');
    const [email, setEmail] = useState('');
    const [sessionId, setSessionId] = useState(null);
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [category, setCategory] = useState('');
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [feedbackPhotos, setFeedbackPhotos] = useState([]);
    const [wifiStatus, setWifiStatus] = useState('idle');
    const [redirectDest, setRedirectDest] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { i18n } = useTranslation();

    // Keep PhotoAttach labels in the guest's chosen language
    useEffect(() => {
        if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
    }, [lang, i18n]);

    // Voucher state (uses existing Voucher Engine data)
    const [voucher, setVoucher] = useState(null);
    const [voucherSaved, setVoucherSaved] = useState(false);
    const [voucherSaving, setVoucherSaving] = useState(false);
    const voucherCardRef = useRef(null);
    const shareCapable = typeof navigator !== 'undefined' && !!navigator.share;

    // Trilingual text — all guest-facing strings use "rating" not "review"
    const text = {
        en: {
            welcome: 'Welcome to',
            emailPrompt: 'Enter the email address you used when checking in.',
            emailPromptSimple: 'Enter your email address to continue.',
            emailPlaceholder: 'your@email.com',
            continue: 'Continue',
            generateWifi: 'Generate Wi-Fi Password',
            settingUpWifi: 'Setting up Wi-Fi credentials\u2026',
            wifiSent: 'Wi-Fi credentials sent to your email',
            rateStay: 'How was your check-in and first impressions?',
            rateOverall: 'How was your check-in and first impressions?',
            rateCheckout: 'How was your overall stay with us?',
            rateInstay: 'How are we doing? Let us know about your stay.',
            gladEnjoy: "We're glad you're enjoying your stay!",
            gladStay: "We're glad you enjoyed your stay!",
            getGift: 'Get a Gift From Us',
            leaveRating: 'Leave a Public Rating',
            pleaseLeaveRating: 'Please leave us a public rating.',
            thankYou: 'Thank you so much!',
            wifiSentConfirm: 'Your Wi-Fi credentials have been sent to your email.',
            depositKeys: 'You can now deposit your key cards in the box.',
            feedbackTitle: 'We appreciate your feedback',
            feedbackPrompt: 'Please help us improve. What area needs attention?',
            feedbackPlaceholder: 'Tell us more about your experience\u2026',
            submitFeedback: 'Submit Feedback',
            feedbackThanks: 'Thank you for your feedback. We will address this promptly.',
            wifiOnly: 'Get your Wi-Fi access',
            wifiOnlyPrompt: 'Enter your email to receive your Wi-Fi credentials.',
            categories: { service: 'Service', food: 'Food', cleanliness: 'Cleanliness', amenities: 'Amenities', other: 'Other' },
            positiveThankOnly: 'Thank you for your feedback!',
            feedbackPhotoHelper: 'Share a photo to help us better understand your experience. Examples: cleanliness concerns, room or property issues, maintenance, or amenities.',
            // Voucher flow strings
            gift: 'Gift',
            code: 'Code',
            expires: 'Expires',
            saveToPhotos: 'Save Voucher to Photos',
            downloadVoucher: 'Download Voucher',
            saving: 'Saving\u2026',
            savedToCameraRoll: 'Gift Saved to Camera Roll',
            giftSaved: 'Gift Saved',
            continueToRating: 'Continue to Leave a Public Rating',
            wifiSentRedirecting: 'Your Wi-Fi credentials have been sent to your email. Redirecting to public rating\u2026',
            redirectingToRating: 'Redirecting to leave your public rating\u2026',
            thankYouSoMuch: 'Thank you so much!',
            wifiSentToEmail: 'Your Wi-Fi credentials have been sent to your email.',
            wifiFailed: "We couldn't send your Wi-Fi credentials — please try again or ask the front desk.",
            wifiRetry: 'Try again',
            continuePublicRating: 'Continue to public rating',
            takingYouTo: "We're taking you to",
            toLeaveYourRating: 'to leave your rating.',
            done: 'Done',
            wifiPortal: 'WIFI PORTAL',
        },
        fr: {
            welcome: 'Bienvenue au',
            emailPrompt: "Entrez l'adresse courriel utilis\u00e9e lors de votre enregistrement.",
            emailPromptSimple: 'Entrez votre adresse courriel pour continuer.',
            emailPlaceholder: 'votre@courriel.com',
            continue: 'Continuer',
            generateWifi: 'G\u00e9n\u00e9rer le mot de passe Wi-Fi',
            settingUpWifi: 'Configuration des identifiants Wi-Fi\u2026',
            wifiSent: 'Identifiants Wi-Fi envoy\u00e9s \u00e0 votre courriel',
            rateStay: 'Comment se sont pass\u00e9s votre arriv\u00e9e et vos premi\u00e8res impressions ?',
            rateOverall: 'Comment se sont pass\u00e9s votre arriv\u00e9e et vos premi\u00e8res impressions ?',
            rateCheckout: 'Comment avez-vous trouv\u00e9 votre s\u00e9jour?',
            rateInstay: 'Comment allons-nous? Dites-nous comment se passe votre s\u00e9jour.',
            gladEnjoy: 'Nous sommes ravis que vous appr\u00e9ciiez votre s\u00e9jour!',
            gladStay: 'Nous sommes ravis que vous ayez appr\u00e9ci\u00e9 votre s\u00e9jour!',
            getGift: 'Recevez un cadeau de notre part',
            leaveRating: 'Laisser une \u00e9valuation publique',
            pleaseLeaveRating: 'Veuillez nous laisser une \u00e9valuation publique.',
            thankYou: 'Merci beaucoup!',
            wifiSentConfirm: 'Vos identifiants Wi-Fi ont \u00e9t\u00e9 envoy\u00e9s \u00e0 votre courriel.',
            depositKeys: 'Vous pouvez maintenant d\u00e9poser vos cartes-cl\u00e9s dans la bo\u00eete.',
            feedbackTitle: 'Nous appr\u00e9cions vos commentaires',
            feedbackPrompt: "Aidez-nous \u00e0 nous am\u00e9liorer. Quel domaine n\u00e9cessite de l'attention?",
            feedbackPlaceholder: 'Dites-nous en plus sur votre exp\u00e9rience\u2026',
            submitFeedback: 'Soumettre les commentaires',
            feedbackThanks: 'Merci pour vos commentaires. Nous y r\u00e9pondrons rapidement.',
            wifiOnly: 'Acc\u00e9dez \u00e0 votre Wi-Fi',
            wifiOnlyPrompt: 'Entrez votre courriel pour recevoir vos identifiants Wi-Fi.',
            categories: { service: 'Service', food: 'Nourriture', cleanliness: 'Propret\u00e9', amenities: 'Commodit\u00e9s', other: 'Autre' },
            positiveThankOnly: 'Merci pour vos commentaires!',
            feedbackPhotoHelper: 'Partagez une photo pour nous aider \u00e0 mieux comprendre votre exp\u00e9rience. Exemples\u00a0: probl\u00e8mes de propret\u00e9, probl\u00e8mes de chambre ou d\u2019\u00e9tablissement, entretien ou commodit\u00e9s.',
            gift: 'Cadeau',
            code: 'Code',
            expires: 'Expire le',
            saveToPhotos: 'Enregistrer le bon dans Photos',
            downloadVoucher: 'T\u00e9l\u00e9charger le bon',
            saving: 'Enregistrement\u2026',
            savedToCameraRoll: 'Cadeau enregistr\u00e9 dans la Pellicule',
            giftSaved: 'Cadeau enregistr\u00e9',
            continueToRating: 'Continuer pour laisser une \u00e9valuation publique',
            wifiSentRedirecting: 'Vos identifiants Wi-Fi ont \u00e9t\u00e9 envoy\u00e9s \u00e0 votre courriel. Redirection vers l\u2019\u00e9valuation publique\u2026',
            redirectingToRating: 'Redirection pour laisser votre \u00e9valuation publique\u2026',
            thankYouSoMuch: 'Merci infiniment\u00a0!',
            wifiSentToEmail: 'Vos identifiants Wi-Fi ont \u00e9t\u00e9 envoy\u00e9s \u00e0 votre courriel.',
            wifiFailed: 'Nous n\u2019avons pas pu envoyer vos identifiants Wi-Fi — veuillez r\u00e9essayer ou vous adresser \u00e0 la r\u00e9ception.',
            wifiRetry: 'R\u00e9essayer',
            continuePublicRating: 'Continuer vers l\u2019\u00e9valuation publique',
            takingYouTo: 'Nous vous redirigeons vers',
            toLeaveYourRating: 'pour laisser votre \u00e9valuation.',
            done: 'Termin\u00e9',
            wifiPortal: 'PORTAIL WI-FI',
        },
        es: {
            welcome: 'Bienvenido a',
            emailPrompt: 'Ingrese el correo electr\u00f3nico que us\u00f3 al registrarse.',
            emailPromptSimple: 'Ingrese su correo electr\u00f3nico para continuar.',
            emailPlaceholder: 'su@correo.com',
            continue: 'Continuar',
            generateWifi: 'Generar contrase\u00f1a Wi-Fi',
            settingUpWifi: 'Configurando credenciales Wi-Fi\u2026',
            wifiSent: 'Credenciales Wi-Fi enviadas a su correo',
            rateStay: '\u00bfC\u00f3mo fue su llegada y cu\u00e1les fueron sus primeras impresiones?',
            rateOverall: '\u00bfC\u00f3mo fue su llegada y cu\u00e1les fueron sus primeras impresiones?',
            rateCheckout: '\u00bfC\u00f3mo fue su estad\u00eda con nosotros?',
            rateInstay: '\u00bfC\u00f3mo lo estamos haciendo? Cu\u00e9ntenos sobre su estad\u00eda.',
            gladEnjoy: '\u00a1Nos alegra que est\u00e9 disfrutando su estad\u00eda!',
            gladStay: '\u00a1Nos alegra que haya disfrutado su estad\u00eda!',
            getGift: 'Reciba un regalo de nuestra parte',
            leaveRating: 'Dejar una calificaci\u00f3n p\u00fablica',
            pleaseLeaveRating: 'Por favor d\u00e9jenos una calificaci\u00f3n p\u00fablica.',
            thankYou: '\u00a1Muchas gracias!',
            wifiSentConfirm: 'Sus credenciales Wi-Fi han sido enviadas a su correo.',
            depositKeys: 'Ahora puede depositar sus tarjetas llave en la caja.',
            feedbackTitle: 'Apreciamos sus comentarios',
            feedbackPrompt: '\u00bfQu\u00e9 \u00e1rea necesita atenci\u00f3n?',
            feedbackPlaceholder: 'Cu\u00e9ntenos m\u00e1s sobre su experiencia\u2026',
            submitFeedback: 'Enviar comentarios',
            feedbackThanks: 'Gracias por sus comentarios. Los atenderemos prontamente.',
            wifiOnly: 'Acceda a su Wi-Fi',
            wifiOnlyPrompt: 'Ingrese su correo para recibir sus credenciales Wi-Fi.',
            categories: { service: 'Servicio', food: 'Comida', cleanliness: 'Limpieza', amenities: 'Comodidades', other: 'Otro' },
            positiveThankOnly: '\u00a1Gracias por sus comentarios!',
            feedbackPhotoHelper: 'Comparta una foto para ayudarnos a comprender mejor su experiencia. Ejemplos: problemas de limpieza, problemas de la habitaci\u00f3n o de la propiedad, mantenimiento o comodidades.',
            gift: 'Regalo',
            code: 'C\u00f3digo',
            expires: 'Vence el',
            saveToPhotos: 'Guardar vale en Fotos',
            downloadVoucher: 'Descargar vale',
            saving: 'Guardando\u2026',
            savedToCameraRoll: 'Regalo guardado en el Carrete',
            giftSaved: 'Regalo guardado',
            continueToRating: 'Continuar para dejar una calificaci\u00f3n p\u00fablica',
            wifiSentRedirecting: 'Sus credenciales Wi-Fi han sido enviadas a su correo. Redirigiendo a la calificaci\u00f3n p\u00fablica\u2026',
            redirectingToRating: 'Redirigiendo para dejar su calificaci\u00f3n p\u00fablica\u2026',
            thankYouSoMuch: '\u00a1Muchas gracias!',
            wifiSentToEmail: 'Sus credenciales Wi-Fi han sido enviadas a su correo electr\u00f3nico.',
            wifiFailed: 'No pudimos enviar sus credenciales Wi-Fi — int\u00e9ntelo de nuevo o consulte en recepci\u00f3n.',
            wifiRetry: 'Reintentar',
            continuePublicRating: 'Continuar a la calificaci\u00f3n p\u00fablica',
            takingYouTo: 'Lo estamos llevando a',
            toLeaveYourRating: 'para dejar su calificaci\u00f3n.',
            done: 'Listo',
            wifiPortal: 'PORTAL WI-FI',
        },
    };

    const tx = text[lang] || text.en;
    // Low-rating "what needs attention?" categories: prefer the org's active
    // Review Shield Question Set (trilingual, e.g. an Auto Dealer set) so every
    // vertical shows its OWN configured categories; fall back to the built-in
    // defaults when the org has none. Category value submitted = the canonical
    // English label so private-feedback reporting stays human-readable.
    const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'other';
    const feedbackCats = (config?.feedback_categories && config.feedback_categories.length)
        ? config.feedback_categories.map((c) => ({ key: c.en, label: c[lang] || c.en }))
        : Object.entries(tx.categories).map(([key, label]) => ({ key, label }));
    const tp = config?.touchpoint_type || 'welcome';
    const wifiOn = config?.wifi_enabled !== false;
    const ratingOn = config?.rating_enabled !== false;
    const giftOn = config?.gift_enabled !== false;
    const publicRatingOn = config?.public_rating_enabled !== false;
    const isCheckout = tp === 'checkout';
    const isLobbyWifi = tp === 'lobby_wifi';
    // Only a hospitality org has a check-in; every other vertical (dealership,
    // property, restaurant) gets the plain "enter your email" wording.
    // `checkout` / `in_stay` are hotel-only touchpoints, so they keep it too.
    const isHospitality = ['hotel', 'hotels', 'hospitality', 'hotel_management']
        .includes(String(config?.vertical || '').toLowerCase()) || isCheckout || tp === 'in_stay';
    const isInStay = tp === 'in_stay';

    const t = useCallback((key) => {
        if (!config) return '';
        const val = config[`${key}_${lang}`];
        if (val) return val;
        return config[`${key}_en`] || '';
    }, [config, lang]);

    const platformName = useCallback(() => {
        const p = config?.platform || '';
        if (p.includes('tripadvisor')) return 'TripAdvisor';
        if (p.includes('facebook')) return 'Facebook';
        if (p.includes('yelp')) return 'Yelp';
        return 'Google';
    }, [config]);

    useEffect(() => {
        fetch(`${API}/api/hotel/${cardId}/config`)
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(data => { setConfig(data); determineFirstScreen(data); })
            .catch(() => setScreen('error'));
    }, [cardId]);

    // Auto-complete session when on positive screen with no further actions
    useEffect(() => {
        if (screen === 'positive' && !giftOn && !publicRatingOn && sessionId) {
            fetch(`${API}/api/hotel/${cardId}/complete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId }),
            }).catch(() => {});
            if (wifiOn) {
                requestWifi(sessionId);
            }
        }
    }, [screen, giftOn, publicRatingOn, sessionId, wifiOn, cardId]);

    const determineFirstScreen = (cfg) => {
        const type = cfg?.touchpoint_type || 'welcome';
        if (type === 'checkout') {
            setScreen('checkout_rating');
        } else if (type === 'lobby_wifi') {
            setScreen('welcome');
        } else if (type === 'in_stay') {
            if (cfg?.wifi_enabled) setScreen('welcome');
            else setScreen('instay_rating');
        } else {
            setScreen('welcome');
        }
    };

    // Sends (or re-sends) the Wi-Fi credentials email. Backend durably queues
    // the job and is idempotent: one session = one email, retries never duplicate.
    const wifiPollRef = useRef(0);
    const pollWifiStatus = async (sid) => {
        const pollId = ++wifiPollRef.current;
        for (let i = 0; i < 30; i++) {
            await new Promise(r => setTimeout(r, 2500));
            if (wifiPollRef.current !== pollId) return false; // superseded by a newer request
            try {
                const r = await fetch(`${API}/api/hotel/${cardId}/wifi-status?session_id=${sid}`);
                const d = await r.json().catch(() => ({}));
                if (d.status === 'sent') { setWifiStatus('sent'); return true; }
                if (d.status === 'failed') { setWifiStatus('failed'); return false; }
            } catch (e) { /* keep polling */ }
        }
        if (wifiPollRef.current === pollId) setWifiStatus('failed');
        return false;
    };

    const requestWifi = async (sid) => {
        if (!sid || !wifiOn) return false;
        setWifiStatus('setting_up');
        try {
            const wr = await fetch(`${API}/api/hotel/${cardId}/wifi-request`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sid }),
            });
            const wd = await wr.json().catch(() => ({}));
            if (wd.wifi_sent || wd.status === 'already_sent' || wd.status === 'sent') {
                setWifiStatus('sent');
                return true;
            }
            if (wd.status === 'queued') return await pollWifiStatus(sid);
            setWifiStatus('failed');
            return false;
        } catch (e) {
            setWifiStatus('failed');
            return false;
        }
    };

    const handleWifiRetry = () => requestWifi(sessionId);

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/hotel/${cardId}/start`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });
            if (!res.ok) throw new Error('start failed');
            const data = await res.json();
            setSessionId(data.session_id);

            if (wifiOn && !ratingOn) {
                // WiFi only (lobby) — queue now; polling flips the status to 'sent'
                setScreen('wifi_only_sent');
                requestWifi(data.session_id);
            } else {
                // WiFi + Rating — start Wi-Fi setup indicator (stays visible until guest completes flow)
                if (wifiOn) {
                    setWifiStatus('setting_up');
                }
                setScreen('wifi_rating');
            }
        } catch (e) { /* retry */ } finally { setSubmitting(false); }
    };

    const handleWifiGenerate = () => setWifiStatus('setting_up');

    const handleRatingSubmit = async (star) => {
        setRating(star);
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/hotel/${cardId}/rate`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating: star, session_id: sessionId }),
            });
            if (!res.ok) throw new Error('rate failed');
            const data = await res.json();
            if (data.is_positive) {
                if (isCheckout) setScreen('checkout_positive');
                else setScreen('positive');
            } else {
                setScreen('negative_feedback');
            }
        } catch (e) { /* retry */ } finally { setSubmitting(false); }
    };

    const handleCheckoutRating = async (star) => {
        setRating(star);
        setSubmitting(true);
        try {
            let sid = sessionId;
            if (!sid) {
                const startRes = await fetch(`${API}/api/hotel/${cardId}/start`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                });
                if (!startRes.ok) throw new Error('start failed');
                const startData = await startRes.json();
                sid = startData.session_id;
                setSessionId(sid);
            }
            const res = await fetch(`${API}/api/hotel/${cardId}/rate`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating: star, session_id: sid }),
            });
            if (!res.ok) throw new Error('rate failed');
            const data = await res.json();
            if (data.is_positive) {
                if (isCheckout) setScreen('checkout_positive');
                else setScreen('positive');
            } else {
                setScreen('negative_feedback');
            }
        } catch (e) { /* retry */ } finally { setSubmitting(false); }
    };

    // ── Gift claim using the EXISTING Voucher Engine ──
    const handleGiftClaim = async () => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/hotel/${cardId}/gift-claim`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId }),
            });
            if (!res.ok) throw new Error('gift claim failed');
            const data = await res.json();
            // Store voucher data from the existing Voucher Engine
            setVoucher({
                code: data.code,
                value: data.value,
                organization_name: data.organization_name,
                expires_at: data.expires_at,
                redeemable_from: data.redeemable_from,
                issuing_org_id: data.issuing_org_id,
            });
            setScreen('voucher_display');
            // NO auto-redirect — guest controls all transitions
        } catch (e) { /* retry */ } finally { setSubmitting(false); }
    };

    // ── Save Voucher to Photos (uses production image endpoint with Issuing Business logo) ──
    const handleSaveVoucherToPhotos = async () => {
        if (!voucher) return;
        setVoucherSaving(true);
        try {
            // Fetch server-generated voucher image (includes Issuing Business logo via production pipeline)
            const res = await fetch(`${API}/api/vouchers/image/${voucher.code}`);
            if (!res.ok) throw new Error('Image fetch failed');
            const blob = await res.blob();
            const fileName = `voucher-${voucher.code}.png`;

            if (navigator.share) {
                const file = new File([blob], fileName, { type: 'image/png' });
                await navigator.share({ files: [file] });
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
            setVoucherSaved(true);
        } catch (e) {
            if (e.name !== 'AbortError') {
                // Fallback: client-side canvas rendering (without logo)
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = 600;
                    canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#0f172a';
                    ctx.fillRect(0, 0, 600, 400);
                    ctx.fillStyle = '#6ee7b7';
                    ctx.font = 'bold 32px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText(`$${voucher.value} ${tx.gift}`, 300, 100);
                    ctx.fillStyle = '#cbd5e1';
                    ctx.font = '18px sans-serif';
                    ctx.fillText(voucher.organization_name || '', 300, 140);
                    ctx.fillStyle = '#6ee7b7';
                    ctx.font = 'bold 28px monospace';
                    ctx.fillText(voucher.code, 300, 220);
                    ctx.fillStyle = '#94a3b8';
                    ctx.font = '14px sans-serif';
                    ctx.fillText(`${tx.expires} ${voucher.expires_at || ''}`, 300, 280);
                    const blob2 = await new Promise(r => canvas.toBlob(r, 'image/png'));
                    const url = URL.createObjectURL(blob2);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `voucher-${voucher.code}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    setVoucherSaved(true);
                } catch (e2) { /* give up silently */ }
            }
        } finally {
            setVoucherSaving(false);
        }
    };

    // ── Leave Public Rating (guest manually presses AFTER saving voucher) ──
    const handleLeavePublicRating = async () => {
        setSubmitting(true);
        // Show intermediate Wi-Fi confirmation + redirect message
        setRedirectDest(platformName());
        setScreen('gift_redirect');
        try {
            if (wifiOn) {
                requestWifi(sessionId); // durable queue — no need to block the redirect
            }
            await fetch(`${API}/api/hotel/${cardId}/review-click`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId, destination: config?.platform }),
            });
            await fetch(`${API}/api/hotel/${cardId}/complete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId }),
            });
        } catch (e) { /* ignored */ }
        // Redirect after showing the message
        setTimeout(() => {
            if (config?.review_url) window.location.href = config.review_url;
        }, 3000);
    };

    const handleDirectRating = async () => {
        setSubmitting(true);
        // Same completion mechanism as the gift path: deliver Wi-Fi, show the
        // sent confirmation screen, THEN redirect to the public destination.
        setRedirectDest(platformName());
        setScreen('gift_redirect');
        try {
            if (wifiOn) {
                requestWifi(sessionId); // durable queue — no need to block the redirect
            }
            await fetch(`${API}/api/hotel/${cardId}/review-click`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId, destination: config?.platform }),
            });
            await fetch(`${API}/api/hotel/${cardId}/complete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId }),
            });
        } catch (e) { /* ignored */ }
        // Redirect after showing the confirmation message
        setTimeout(() => {
            if (config?.review_url) window.location.href = config.review_url;
        }, 3000);
    };

    const handleCheckoutPublicRating = async () => {
        setRedirectDest(platformName());
        setScreen('checkout_redirect');
        try {
            await fetch(`${API}/api/hotel/${cardId}/review-click`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId, destination: config?.platform }),
            });
            await fetch(`${API}/api/hotel/${cardId}/complete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId }),
            });
        } catch (e) { /* ignored */ }
        setTimeout(() => {
            if (config?.review_url) window.location.href = config.review_url;
        }, 3000);
    };

    const handleNegativeFeedback = async () => {
        setSubmitting(true);
        try {
            if (sessionId && (category || feedbackMsg || feedbackPhotos.length > 0)) {
                if (feedbackPhotos.length > 0) {
                    // Review Shield parity: multipart submission with photos
                    const fd = new FormData();
                    fd.append('payload', JSON.stringify({ session_id: sessionId, category, message: feedbackMsg }));
                    feedbackPhotos.forEach(p => fd.append('photos', p.file, p.file.name));
                    await fetch(`${API}/api/hotel/${cardId}/feedback-with-photos`, { method: 'POST', body: fd });
                } else {
                    await fetch(`${API}/api/hotel/${cardId}/feedback`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ session_id: sessionId, category, message: feedbackMsg }),
                    });
                }
            }
            if (wifiOn && sessionId) {
                requestWifi(sessionId); // durable queue — thanks screen shows live status
            }
            if (sessionId) {
                await fetch(`${API}/api/hotel/${cardId}/complete`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sessionId, outcome_override: 'private_feedback' }),
                });
            }
            setScreen('feedback_thanks');
        } catch (e) { /* retry */ } finally { setSubmitting(false); }
    };

    const logoUrl = config?.logo_url
        ? (config.logo_url.startsWith('/api') ? `${API}${config.logo_url}` : config.logo_url)
        : null;

    // ─── Shared UI pieces ─────────────────────────────────────

    const LangSelector = () => (
        <div className="fixed top-4 right-4 flex gap-1 z-50" data-testid="hotel-lang-selector">
            {['en', 'fr', 'es'].map(l => (
                <button key={l} onClick={() => setLang(l)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${lang === l ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                    data-testid={`hotel-lang-${l}`}>{l.toUpperCase()}</button>
            ))}
        </div>
    );

    const GuestBack = () => (
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            data-testid="hotel-back" aria-label={{ en: 'Back', fr: 'Retour', es: 'Volver' }[lang] || 'Back'}
            className="fixed top-4 left-4 z-50 flex items-center gap-1 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-2.5 py-1.5 transition-colors">
            <ArrowLeft className="w-4 h-4" />
        </button>
    );

    const WifiStatusBadge = () => (
        wifiOn && wifiStatus !== 'idle' && screen !== 'feedback_thanks' ? (
            <div className="fixed top-4 left-16 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 z-50" data-testid="wifi-status">
                {wifiStatus === 'setting_up' && (<><Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" /><span className="text-xs text-amber-300">{tx.settingUpWifi}</span></>)}
                {wifiStatus === 'sent' && (<><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /><span className="text-xs text-emerald-300">{tx.wifiSent}</span></>)}
                {wifiStatus === 'failed' && (<><WifiOff className="w-3.5 h-3.5 text-red-400" /><span className="text-xs text-red-300">{tx.wifiRetry}</span></>)}
            </div>
        ) : null
    );

    const WifiFailedBlock = () => (
        <div className="text-center p-4 rounded-xl bg-red-950/40 border border-red-800/30 space-y-3" data-testid="hotel-wifi-failed">
            <div className="flex items-center justify-center gap-2">
                <WifiOff className="w-5 h-5 text-red-400" />
                <span className="text-red-300 text-sm">{tx.wifiFailed}</span>
            </div>
            <Button onClick={handleWifiRetry} variant="outline"
                className="border-red-700/50 bg-transparent text-red-200 hover:bg-red-900/30 hover:text-red-100 h-9"
                data-testid="hotel-wifi-retry-btn">
                <Wifi className="w-4 h-4 mr-2" />{tx.wifiRetry}
            </Button>
        </div>
    );

    const Logo = () => logoUrl ? (
        <div className="flex justify-center mb-6"><img src={logoUrl} alt="" style={{ height: `${config?.hotel_logo_size || 40}px` }} className="w-auto object-contain rounded-xl" data-testid="hotel-logo" /></div>
    ) : null;

    const StarRating = ({ onRate, disabled }) => (
        <div className="flex justify-center gap-2" data-testid="hotel-star-rating">
            {[1, 2, 3, 4, 5].map(s => (
                <button key={s} onClick={() => onRate(s)} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)}
                    disabled={disabled} className="p-1 transition-transform hover:scale-110" data-testid={`hotel-star-${s}`}>
                    <Star className="w-10 h-10 sm:w-12 sm:h-12"
                        fill={s <= (hoverRating || rating) ? '#FBBF24' : 'transparent'}
                        stroke={s <= (hoverRating || rating) ? '#FBBF24' : '#64748b'} strokeWidth={1.5} />
                </button>
            ))}
        </div>
    );

    const MiniStars = () => (
        <div className="flex justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map(s => (<Star key={s} className="w-6 h-6" fill={s <= rating ? '#FBBF24' : 'transparent'} stroke={s <= rating ? '#FBBF24' : '#64748b'} strokeWidth={1.5} />))}
        </div>
    );

    // ─── Screen rendering ─────────────────────────────────────

    if (screen === 'loading') {
        return (<div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center" data-testid="hotel-loading"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>);
    }
    if (screen === 'error') {
        return (<div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-6" data-testid="hotel-error"><p className="text-slate-400">This hotel experience is not available.</p></div>);
    }

    // ═══ OPTIONAL LANGUAGE GATE (shared Review Shield pattern) — shown BEFORE the welcome/email screen when enabled ═══
    if (config?.language_selection_enabled === true && !langGateDone) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 sm:p-6" data-testid="hotel-language-gate">
                <div className="w-full max-w-md">
                    <Logo />
                    <LanguageGate
                        enabled={['en', 'fr', 'es']}
                        onSelect={(code) => { setLang(code); setLangGateDone(true); }}
                        variant="dark"
                        embedded
                    />
                </div>
            </div>
        );
    }

    // ═══ ROOM FEEDBACK — pure feedback experience (Guest Check-In flow) ═══
    if (tp === 'room_feedback') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 sm:p-6" data-testid="hotel-room-feedback-flow">
                <LangSelector />
                <div className="w-full max-w-md">
                    <Logo />
                    <RoomFeedbackGuestFlow config={config} cardId={cardId} lang={lang} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 sm:p-6" data-testid="hotel-guest-flow">
            <GuestBack />
            <LangSelector />
            <WifiStatusBadge />

            <div className="w-full max-w-md">
                {screen === 'welcome' && wifiOn && (
                    <p className="text-center text-[11px] sm:text-xs font-medium tracking-[0.35em] uppercase text-slate-400 mb-5 animate-in fade-in duration-500"
                        data-testid="hotel-wifi-portal-label">
                        {tx.wifiPortal}
                    </p>
                )}
                <Logo />

                {/* ═══ WELCOME / EMAIL CAPTURE ═══ */}
                {screen === 'welcome' && (
                    <div className="space-y-6 animate-in fade-in duration-500" data-testid="hotel-welcome-screen">
                        <div className="text-center">
                            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                {tx.welcome} {config?.hotel_name}
                            </h1>
                            {t('welcome_message') && <p className="text-slate-300 text-sm">{t('welcome_message')}</p>}
                        </div>
                        <p className="text-slate-400 text-center text-sm">
                            {isLobbyWifi ? tx.wifiOnlyPrompt : (isHospitality ? tx.emailPrompt : tx.emailPromptSimple)}
                        </p>
                        <form onSubmit={handleEmailSubmit} className="space-y-4">
                            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                placeholder={tx.emailPlaceholder} required
                                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-12" data-testid="hotel-email-input" />
                            <Button type="submit" disabled={submitting || !email.includes('@')}
                                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="hotel-continue-btn">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : tx.continue}
                                {!submitting && <ChevronRight className="w-4 h-4 ml-1" />}
                            </Button>
                        </form>
                    </div>
                )}

                {/* ═══ WIFI ONLY SENT (Lobby WiFi) ═══ */}
                {screen === 'wifi_only_sent' && (
                    <div className="space-y-6 animate-in fade-in duration-500 text-center" data-testid="hotel-wifi-only-screen">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                        <h2 className="text-xl font-semibold text-white">{tx.thankYou}</h2>
                        {wifiStatus === 'sent' && <p className="text-slate-300 text-sm">{tx.wifiSentConfirm}</p>}
                        <div className="flex items-center justify-center gap-2 pt-2">
                            {wifiStatus === 'setting_up' && (<><Loader2 className="w-4 h-4 animate-spin text-amber-400" /><span className="text-amber-300 text-sm">{tx.settingUpWifi}</span></>)}
                            {wifiStatus === 'sent' && (<><Wifi className="w-4 h-4 text-emerald-400" /><span className="text-emerald-300 text-sm">{tx.wifiSent}</span></>)}
                        </div>
                        {wifiStatus === 'failed' && <WifiFailedBlock />}
                    </div>
                )}

                {/* ═══ WIFI + RATING (Welcome / In-Stay with WiFi) ═══ */}
                {screen === 'wifi_rating' && (
                    <div className="space-y-8 animate-in fade-in duration-500" data-testid="hotel-wifi-rating-screen">
                        {/* Prominent Wi-Fi generation status */}
                        {wifiOn && wifiStatus === 'setting_up' && (
                            <div className="text-center p-4 rounded-xl bg-blue-950/40 border border-blue-800/30" data-testid="hotel-wifi-generating">
                                <div className="flex items-center justify-center gap-3 mb-2">
                                    <div className="relative">
                                        <Wifi className="w-6 h-6 text-blue-400 animate-pulse" />
                                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-400 rounded-full animate-ping" />
                                    </div>
                                    <span className="text-blue-200 font-medium">{tx.settingUpWifi}</span>
                                </div>
                                <div className="w-48 h-1.5 bg-blue-900/50 rounded-full mx-auto overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-blue-500 to-amber-400 rounded-full animate-pulse" style={{ width: '60%' }} />
                                </div>
                            </div>
                        )}
                        {wifiOn && wifiStatus === 'sent' && (
                            <div className="text-center p-3 rounded-xl bg-emerald-950/30 border border-emerald-800/30" data-testid="hotel-wifi-sent">
                                <div className="flex items-center justify-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                    <span className="text-emerald-300 text-sm">{tx.wifiSent}</span>
                                </div>
                            </div>
                        )}
                        {wifiOn && wifiStatus === 'failed' && <WifiFailedBlock />}
                        {/* Manual Wi-Fi generate button (only if auto-trigger didn't fire) */}
                        {wifiOn && wifiStatus === 'idle' && (
                            <div className="text-center">
                                <Button onClick={handleWifiGenerate}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 h-auto" data-testid="hotel-generate-wifi-btn">
                                    <Wifi className="w-4 h-4 mr-2" />{tx.generateWifi}
                                </Button>
                            </div>
                        )}
                        {ratingOn && (
                            <div className="text-center space-y-6">
                                <p className="text-white text-sm sm:text-base">
                                    {wifiOn && wifiStatus !== 'idle' ? tx.rateStay : (isInStay ? tx.rateInstay : tx.rateOverall)}
                                </p>
                                <StarRating onRate={handleRatingSubmit} disabled={submitting} />
                            </div>
                        )}
                    </div>
                )}

                {/* ═══ IN-STAY RATING (no WiFi) ═══ */}
                {screen === 'instay_rating' && (
                    <div className="space-y-8 animate-in fade-in duration-500 text-center" data-testid="hotel-instay-rating-screen">
                        <h2 className="text-xl font-semibold text-white">{tx.rateInstay}</h2>
                        <StarRating onRate={handleCheckoutRating} disabled={submitting} />
                    </div>
                )}

                {/* ═══ CHECKOUT RATING ═══ */}
                {screen === 'checkout_rating' && (
                    <div className="space-y-8 animate-in fade-in duration-500 text-center" data-testid="hotel-checkout-rating-screen">
                        <h2 className="text-xl sm:text-2xl font-semibold text-white">{tx.rateCheckout}</h2>
                        <StarRating onRate={handleCheckoutRating} disabled={submitting} />
                    </div>
                )}

                {/* ═══ POSITIVE SCREEN (Welcome/In-Stay) ═══ */}
                {screen === 'positive' && (
                    <div className="space-y-6 animate-in fade-in duration-500 text-center" data-testid="hotel-positive-screen">
                        <MiniStars />
                        <h2 className="text-xl font-semibold text-white">{tx.gladEnjoy}</h2>
                        <div className="space-y-3 pt-4">
                            {giftOn && (
                                <button
                                    onClick={handleGiftClaim}
                                    disabled={submitting}
                                    className="w-full h-12 rounded-md font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                                    style={{ background: 'linear-gradient(135deg, #C5960C 0%, #E8D164 50%, #D4A017 100%)' }}
                                    data-testid="hotel-get-gift-btn"
                                >
                                    <Gift className="w-4 h-4" />{tx.getGift}
                                </button>
                            )}
                            {publicRatingOn && (
                                <button
                                    onClick={handleDirectRating}
                                    disabled={submitting}
                                    className="w-full h-12 rounded-md font-semibold text-white flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-60"
                                    data-testid="hotel-direct-review-btn"
                                >
                                    <ExternalLink className="w-4 h-4" />{tx.leaveRating}
                                </button>
                            )}
                            {!giftOn && !publicRatingOn && (
                                <p className="text-slate-300 text-sm pt-2">{tx.positiveThankOnly}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══ CHECKOUT POSITIVE ═══ */}
                {screen === 'checkout_positive' && (
                    <div className="space-y-6 animate-in fade-in duration-500 text-center" data-testid="hotel-checkout-positive-screen">
                        <MiniStars />
                        <h2 className="text-xl font-semibold text-white">{tx.gladStay}</h2>
                        {giftOn ? (
                            <div className="space-y-3 pt-4">
                                <button
                                    onClick={handleGiftClaim}
                                    disabled={submitting}
                                    className="w-full h-12 rounded-md font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                                    style={{ background: 'linear-gradient(135deg, #C5960C 0%, #E8D164 50%, #D4A017 100%)' }}
                                    data-testid="hotel-checkout-gift-btn"
                                >
                                    <Gift className="w-4 h-4" />{tx.getGift}
                                </button>
                                {publicRatingOn && (
                                    <button
                                        onClick={handleCheckoutPublicRating}
                                        disabled={submitting}
                                        className="w-full h-12 rounded-md font-semibold text-white flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-60"
                                        data-testid="hotel-checkout-rating-btn"
                                    >
                                        <ExternalLink className="w-4 h-4" />{tx.leaveRating}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="pt-4">
                                <p className="text-slate-300 text-sm mb-4">{tx.pleaseLeaveRating}</p>
                                <button
                                    onClick={handleCheckoutPublicRating}
                                    disabled={submitting}
                                    className="w-full h-12 rounded-md font-semibold text-white flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-60"
                                    data-testid="hotel-checkout-rating-btn"
                                >
                                    <ExternalLink className="w-4 h-4" />{tx.leaveRating}
                                </button>
                            </div>
                        )}
                        <p className="text-slate-400 text-sm">{tx.depositKeys}</p>
                    </div>
                )}

                {/* ═══ VOUCHER DISPLAY (Existing Voucher Engine) ═══
                    Hotel vouchers are immediately redeemable.
                    Save to Photos → saved confirmation → Continue to Leave a Public Rating.
                    NO automatic redirect. Guest controls all transitions. */}
                {screen === 'voucher_display' && voucher && (
                    <div className="space-y-4 animate-in fade-in duration-300" data-testid="hotel-voucher-screen">
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 sm:p-7 text-center border border-slate-700/50">

                            {/* Voucher card — rendered to canvas for Save to Photos */}
                            <div
                                ref={voucherCardRef}
                                className="my-6 py-6 px-4 rounded-xl bg-gradient-to-br from-emerald-900/60 to-slate-900/80 border-y border-slate-700"
                            >
                                {cardId && (
                                    <img
                                        src={`${API}/api/hotel/logo/${cardId}/voucher?v=${sessionId || Date.now()}`}
                                        alt=""
                                        style={{ height: `${config?.voucher_logo_size || 64}px`, maxWidth: '85%' }}
                                        className="w-auto object-contain mx-auto mb-3"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                        data-testid="hotel-voucher-logo"
                                    />
                                )}
                                {voucher.value != null && (
                                    <div className="text-emerald-300 text-3xl font-bold mb-1">
                                        {`$${Number(voucher.value).toFixed(0)}`}
                                        <span className="text-sm font-normal text-slate-300 ml-2">
                                            {tx.gift}
                                        </span>
                                    </div>
                                )}
                                {voucher.organization_name && (
                                    <div className="text-slate-300 text-sm mb-3">
                                        {voucher.organization_name}
                                    </div>
                                )}
                                <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">{tx.code}</div>
                                <div className="text-3xl font-mono font-bold text-emerald-400" data-testid="hotel-voucher-code">
                                    {voucher.code}
                                </div>
                                {voucher.expires_at && (
                                    <div className="text-xs text-slate-400 mt-3">
                                        {tx.expires}{' '}
                                        {new Date(voucher.expires_at).toLocaleDateString()}
                                    </div>
                                )}
                            </div>

                            {/* Save to Photos / Saved confirmation */}
                            {voucherSaved ? (
                                <div
                                    className="w-full py-4 min-h-[60px] rounded-xl bg-emerald-900/40 border border-emerald-700/50 text-emerald-200 font-semibold text-lg mb-3 flex items-center justify-center gap-2"
                                    role="status"
                                    aria-live="polite"
                                    data-testid="hotel-voucher-saved-confirmation"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>
                                        {shareCapable ? tx.savedToCameraRoll : tx.giftSaved}
                                    </span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleSaveVoucherToPhotos}
                                    disabled={voucherSaving}
                                    className="w-full py-4 min-h-[60px] rounded-xl font-semibold text-lg transition-colors mb-3 disabled:opacity-60 border border-transparent text-white"
                                    style={{ background: 'linear-gradient(135deg, #C5960C 0%, #E8D164 50%, #D4A017 100%)' }}
                                    data-testid="hotel-voucher-save-btn"
                                >
                                    {voucherSaving
                                        ? tx.saving
                                        : shareCapable
                                            ? tx.saveToPhotos
                                            : tx.downloadVoucher}
                                </button>
                            )}

                            {/* Continue to Leave a Public Rating — ONLY visible AFTER voucher is saved */}
                            {publicRatingOn && voucherSaved && (
                                <button
                                    type="button"
                                    onClick={handleLeavePublicRating}
                                    disabled={submitting}
                                    className="w-full py-4 rounded-xl font-semibold text-lg transition-colors mb-2 shadow-lg bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="hotel-voucher-leave-rating-btn"
                                >
                                    {tx.continueToRating}
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="w-full py-3 rounded-xl text-slate-400 hover:text-slate-200 font-medium transition-colors"
                                data-testid="hotel-voucher-done-btn"
                            >
                                {tx.done}
                            </button>
                        </div>
                    </div>
                )}

                {/* ═══ GIFT REDIRECT (Prominent Wi-Fi confirmation + redirect to public rating) ═══ */}
                {screen === 'gift_redirect' && (
                    <div className="space-y-8 animate-in fade-in duration-500 text-center py-4" data-testid="hotel-gift-redirect-screen">
                        <div className="bg-emerald-900/30 border border-emerald-700/40 rounded-2xl p-8 space-y-5">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-white">{tx.thankYouSoMuch}</h2>
                            {wifiOn && wifiStatus === 'sent' && (
                                <p className="text-emerald-200 text-base font-medium leading-relaxed">
                                    {tx.wifiSentToEmail}
                                </p>
                            )}
                            {wifiOn && wifiStatus === 'failed' && (
                                <p className="text-red-300 text-sm leading-relaxed" data-testid="hotel-wifi-failed-redirect">
                                    {tx.wifiFailed}
                                </p>
                            )}
                            <div className="pt-2 flex items-center justify-center gap-3">
                                <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                                <p className="text-amber-300 font-medium">{tx.redirectingToRating}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ CHECKOUT REDIRECT ═══ */}
                {screen === 'checkout_redirect' && (
                    <div className="space-y-6 animate-in fade-in duration-500 text-center" data-testid="hotel-checkout-redirect-screen">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                        <h2 className="text-xl font-semibold text-white">{tx.thankYou}</h2>
                        <div className="flex items-center justify-center gap-2 pt-2">
                            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                            <p className="text-amber-300 text-sm">{tx.takingYouTo} {redirectDest} {tx.toLeaveYourRating}</p>
                        </div>
                        <p className="text-slate-400 text-sm">{tx.depositKeys}</p>
                    </div>
                )}

                {/* ═══ NEGATIVE FEEDBACK ═══ */}
                {screen === 'negative_feedback' && (
                    <div className="space-y-6 animate-in fade-in duration-500" data-testid="hotel-negative-feedback-screen">
                        <MiniStars />
                        <h2 className="text-lg font-semibold text-white text-center">{tx.feedbackTitle}</h2>
                        <p className="text-slate-400 text-sm text-center">{tx.feedbackPrompt}</p>
                        <div className="flex flex-wrap justify-center gap-2">
                            {feedbackCats.map(({ key, label }) => (
                                <button key={key} onClick={() => setCategory(key)}
                                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${category === key ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                                    data-testid={`hotel-category-${slugify(key)}`}>{label}</button>
                            ))}
                        </div>
                        <textarea value={feedbackMsg} onChange={e => setFeedbackMsg(e.target.value)}
                            placeholder={tx.feedbackPlaceholder} rows={3}
                            className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder:text-white/40 text-sm resize-none"
                            data-testid="hotel-feedback-textarea" />
                        <PhotoAttach photos={feedbackPhotos} onChange={setFeedbackPhotos} helperText={tx.feedbackPhotoHelper} />
                        <Button onClick={handleNegativeFeedback} disabled={submitting}
                            className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="hotel-submit-feedback-btn">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : tx.submitFeedback}
                        </Button>
                    </div>
                )}

                {/* ═══ FEEDBACK THANKS ═══ */}
                {screen === 'feedback_thanks' && (
                    <div className="space-y-6 animate-in fade-in duration-500 text-center" data-testid="hotel-feedback-thanks-screen">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                        <h2 className="text-lg font-semibold text-white">{tx.thankYou}</h2>
                        <p className="text-slate-300 text-sm">{tx.feedbackThanks}</p>
                        {wifiOn && wifiStatus === 'sent' && (
                            <div className="flex items-center justify-center gap-2 pt-2">
                                <Wifi className="w-4 h-4 text-emerald-400" />
                                <span className="text-emerald-300 text-sm">{tx.wifiSentToEmail}</span>
                            </div>
                        )}
                        {wifiOn && wifiStatus === 'failed' && <WifiFailedBlock />}
                        {/* Public rating is ALWAYS available — never gated by the rating given (Review Shield parity) */}
                        {config?.review_url && (
                            <button
                                onClick={async () => {
                                    try {
                                        await fetch(`${API}/api/hotel/${cardId}/review-click`, {
                                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ session_id: sessionId, destination: config?.platform }),
                                        });
                                    } catch (e) { /* ignored */ }
                                    window.location.href = config.review_url;
                                }}
                                className="text-slate-400 text-sm underline underline-offset-4 hover:text-slate-200 transition-colors pt-1"
                                data-testid="hotel-thanks-public-rating-link"
                            >
                                {tx.continuePublicRating}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HotelGuestFlow;
